/**
 * Роли и права. Роль даёт базовый набор прав; отдельные права администратор
 * может выдать конкретному пользователю (например, запуск командировок за другого).
 * Сервер проверяет права на каждом запросе; интерфейс только прячет недоступное.
 */

export const ROLES = ['employee', 'manager', 'clerk', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  employee: 'Сотрудник',
  manager: 'Руководитель',
  clerk: 'Делопроизводитель',
  admin: 'Администратор',
};

export const PERMISSIONS = {
  'users.read': 'Просмотр списка пользователей',
  'users.manage': 'Создание и изменение пользователей',
  'routes.manage': 'Создание и изменение маршрутов согласования',
  'requests.view_all': 'Просмотр всех задач и заявок',
  'requests.override': 'Замена согласующих и принудительная смена статусов',
  'subtasks.create_any': 'Создание подзадач в любой задаче',
  'records.delete': 'Удаление задач, маршрутов и типов заявок навсегда',
  'notifications.broadcast': 'Рассылка уведомлений сотрудникам',
  'trips.on_behalf': 'Запуск командировок за другого сотрудника',
  'request_types.manage': 'Настройка типов заявок и обязательных полей',
  'templates.manage': 'Шаблоны документов',
  'regulations.manage': 'Регулярные задачи: создание и настройка',
  'guides.manage': 'Инструкции: загрузка и выгрузка настроек для их подготовки',
  'news.manage': 'Публикация новостей',
  'reports.view': 'Отчёты и статистика по всей организации',
  'audit.read': 'Журнал действий',
  'email_log.read': 'Журнал писем',
  'settings.manage': 'Системные настройки',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as Permission[];

const EMPLOYEE: Permission[] = ['users.read'];
const MANAGER: Permission[] = [...EMPLOYEE, 'routes.manage'];
const CLERK: Permission[] = [
  ...EMPLOYEE,
  'requests.view_all',
  'subtasks.create_any',
  'notifications.broadcast',
];

/** Права, которые даёт роль. Права сверх этого выдаются пользователю отдельно. */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  employee: EMPLOYEE,
  manager: MANAGER,
  clerk: CLERK,
  admin: PERMISSION_KEYS,
};

export function effectivePermissions(role: Role, granted: readonly string[] = []): Permission[] {
  const set = new Set<Permission>(ROLE_PERMISSIONS[role]);
  for (const p of granted) {
    if (p in PERMISSIONS) set.add(p as Permission);
  }
  return [...set].sort();
}
